/**
 * urlAllowlist — Super-Admin-managed allowlist of URL patterns that
 * AutoCreate-from-URL is permitted to fetch on the org/tenant's behalf.
 *
 * Why this exists
 * ───────────────
 * The defense-in-depth URL validator (`utils/validateExternalUrl`)
 * blocks SSRF, private ranges, metadata endpoints, and unsafe schemes.
 * That stops a casual abuser. It does NOT stop a determined operator
 * from fetching, say, a competitor's documentation portal or a public
 * CDN that happens to host policy-shaped content the org doesn't want
 * processed.
 *
 * The Super Admin therefore enumerates ahead of time the URL patterns
 * that ARE acceptable for their org/tenant. AutoCreate-from-URL is
 * GATED CLOSED until that list is non-empty AND the user-submitted
 * URL matches at least one pattern.
 *
 * Storage model
 * ─────────────
 * Persisted in `localStorage` under the key
 *   agentlets.url-allowlist.<orgId>.<tenantId>
 * as a JSON array of pattern strings. Two pattern shapes are accepted:
 *   1. Exact URL  — `https://docs.example.com/runbook/incident-response`
 *   2. Glob       — `https://docs.example.com/runbook/*` (one trailing
 *                   `*` only, matches any path under that prefix). The
 *                   wildcard MUST end the pattern; mid-pattern `*` is
 *                   rejected because it makes inspection harder.
 *
 * Hostname-only patterns (`https://docs.example.com`) are auto-expanded
 * to `https://docs.example.com/*` to allow common author intent.
 *
 * Future migration
 * ────────────────
 * When a backend table for this lands (per-tenant URL allowlist column),
 * this module's API stays stable — only the storage backing flips from
 * localStorage to API calls. Callers won't change.
 */

const KEY_PREFIX = 'agentlets.url-allowlist';

function storageKey(orgId, tenantId) {
  // Both ids may be undefined in dev sessions; fall back to 'global'
  // so the allowlist still persists across reloads.
  return `${KEY_PREFIX}.${orgId || 'global'}.${tenantId || 'global'}`;
}

function safeParse(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Read the allowlist for the given scope.
 * @returns {string[]} normalised pattern strings (sorted, unique).
 */
export function getUrlAllowlist(orgId, tenantId) {
  if (typeof localStorage === 'undefined') return [];
  return safeParse(localStorage.getItem(storageKey(orgId, tenantId)));
}

/**
 * Write the allowlist. Patterns are normalised — duplicates removed,
 * sorted alphabetically, hostname-only entries auto-expanded with `/*`.
 *
 * Caller is responsible for verifying the actor is Super Admin BEFORE
 * invoking this. We do not enforce role here (utils layer).
 */
export function setUrlAllowlist(orgId, tenantId, patterns) {
  if (typeof localStorage === 'undefined') return;
  const cleaned = Array.from(new Set(
    (patterns || [])
      .map((p) => typeof p === 'string' ? p.trim() : '')
      .filter(Boolean)
      .map(normalisePattern)
  )).sort();
  localStorage.setItem(storageKey(orgId, tenantId), JSON.stringify(cleaned));
}

/**
 * Normalise a pattern for storage:
 *   - lowercase the scheme + hostname (URL spec is case-insensitive there)
 *   - drop trailing slash unless followed by `*`
 *   - auto-append `/*` when the user typed only a hostname
 */
export function normalisePattern(input) {
  if (typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  // Pattern must start with https:// — never allow http for allowlist
  // entries either, even though the user is the one submitting them.
  if (!/^https:\/\//i.test(trimmed)) return trimmed;
  try {
    // Treat the path as opaque to URL — strip the trailing wildcard,
    // canonicalise the rest, then re-attach.
    const hasGlob = trimmed.endsWith('/*') || trimmed.endsWith('*');
    const baseStr = hasGlob ? trimmed.replace(/\/?\*$/, '') : trimmed;
    const u = new URL(baseStr);
    // Lowercase scheme + host.
    const scheme = u.protocol.toLowerCase();
    const host = u.hostname.toLowerCase();
    const port = u.port ? `:${u.port}` : '';
    // Path: empty pathname plus glob → "/*"; otherwise keep as-is.
    let path = u.pathname || '';
    if (hasGlob) {
      path = path.replace(/\/?$/, '/*');
    } else if (!path) {
      path = '/*';
    }
    const search = u.search || '';
    return `${scheme}//${host}${port}${path}${search}`;
  } catch {
    return trimmed;
  }
}

/**
 * Check if a (validated) URL matches at least one allowlist pattern.
 *
 * The `url` argument is expected to have already passed
 * validateExternalUrl — that's the structural / SSRF gate. THIS check
 * is the per-tenant policy gate.
 *
 * @returns {{ok: true} | {ok: false, reason: string}}
 */
export function isUrlAllowed(url, orgId, tenantId) {
  const list = getUrlAllowlist(orgId, tenantId);
  if (list.length === 0) {
    return {
      ok: false,
      reason: 'AutoCreate-from-URL is disabled — Super Admin has not approved any URLs for this tenant. Configure the allowlist in Admin Center → URL Allowlist.',
    };
  }
  let parsed;
  try { parsed = new URL(url); } catch {
    return { ok: false, reason: 'URL is not parseable for allowlist check.' };
  }
  for (const pattern of list) {
    if (matchesPattern(parsed, pattern)) {
      return { ok: true };
    }
  }
  return {
    ok: false,
    reason: `URL is not in this tenant's allowlist (${list.length} pattern${list.length === 1 ? '' : 's'} configured). Ask a Super Admin to add it.`,
  };
}

function matchesPattern(parsedUrl, pattern) {
  if (typeof pattern !== 'string') return false;
  // Hard equality first.
  if (parsedUrl.toString() === pattern) return true;
  // Glob pattern — must end in /* (after normalisePattern). Match by
  // verifying the URL starts with the prefix.
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -1); // keep the trailing slash
    return parsedUrl.toString().startsWith(prefix);
  }
  return false;
}

export default { getUrlAllowlist, setUrlAllowlist, normalisePattern, isUrlAllowed };
